import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { money, fmtDate } from "@/mechanic/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertTriangle, Car, Building2, Plus, Pencil, Trash2, DollarSign, Check,
} from "lucide-react";
import { toast } from "sonner";

const ALL_TAB = "__all__";
const UNASSIGNED_TAB = "__unassigned__";
const BALANCES_TAB = "__balances__";

export default function RentalFleet() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState(ALL_TAB);
  const [manageOpen, setManageOpen] = useState(false);
  const [assignFor, setAssignFor] = useState(null); // vehicle row
  const [paymentFor, setPaymentFor] = useState(null); // balance row
  const [historyFor, setHistoryFor] = useState(null); // balance row

  const { data: cars = [], isLoading } = useQuery({
    queryKey: ["mech-rental"],
    queryFn: async () => (await api.get("/rental-fleet")).data,
  });
  const { data: companies = [] } = useQuery({
    queryKey: ["mech-companies"],
    queryFn: async () => (await api.get("/companies")).data,
  });
  const { data: balances = [] } = useQuery({
    queryKey: ["mech-balances"],
    queryFn: async () => (await api.get("/company-balances")).data,
  });

  const assign = useMutation({
    mutationFn: ({ ref_id, company_id }) =>
      api.put(`/rental-vehicle/${ref_id}/company`, { company_id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mech-rental"] });
      qc.invalidateQueries({ queryKey: ["mech-balances"] });
      toast.success("Car assigned");
      setAssignFor(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const groups = useMemo(() => {
    const g = { assigned: {}, unassigned: [] };
    for (const c of cars) {
      if (c.company_id) {
        (g.assigned[c.company_id] ||= { company_name: c.company_name, cars: [] }).cars.push(c);
      } else {
        g.unassigned.push(c);
      }
    }
    return g;
  }, [cars]);

  const visibleCars = useMemo(() => {
    if (tab === ALL_TAB) return cars;
    if (tab === UNASSIGNED_TAB) return groups.unassigned;
    return cars.filter((c) => c.company_id === tab);
  }, [tab, cars, groups]);

  const tabs = useMemo(() => {
    // Only show tabs for companies that actually have cars in the fleet.
    const withCars = companies
      .map((co) => ({ ...co, count: (groups.assigned[co.id]?.cars || []).length }))
      .filter((c) => c.count > 0);
    return withCars;
  }, [companies, groups]);

  const totalUnassigned = groups.unassigned.length;

  return (
    <div className="p-8" data-testid="mech-rental-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl mb-1">Rental Fleet</h1>
          <p className="text-muted-foreground">
            Service status by owning company. Cars overdue for an oil change (45+ days) are flagged.
          </p>
        </div>
        <Button variant="outline" onClick={() => setManageOpen(true)} data-testid="mech-manage-companies">
          <Building2 className="h-4 w-4 mr-2" /> Manage companies
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-border mb-5">
        <TabButton active={tab === ALL_TAB} onClick={() => setTab(ALL_TAB)} testid="tab-all">
          All <span className="ml-1.5 text-xs text-muted-foreground">({cars.length})</span>
        </TabButton>
        {tabs.map((co) => (
          <TabButton key={co.id} active={tab === co.id} onClick={() => setTab(co.id)} testid={`tab-company-${co.id}`}>
            {co.name}
            <span className="ml-1.5 text-xs text-muted-foreground">({co.count})</span>
            {!co.billable && <Badge variant="secondary" className="ml-2 text-[10px]">not billed</Badge>}
          </TabButton>
        ))}
        {totalUnassigned > 0 && (
          <TabButton active={tab === UNASSIGNED_TAB} onClick={() => setTab(UNASSIGNED_TAB)} testid="tab-unassigned">
            Unassigned <span className="ml-1.5 text-xs text-muted-foreground">({totalUnassigned})</span>
          </TabButton>
        )}
        <TabButton active={tab === BALANCES_TAB} onClick={() => setTab(BALANCES_TAB)} testid="tab-balances">
          <DollarSign className="h-3.5 w-3.5 inline mr-1" /> Balances
        </TabButton>
      </div>

      {tab === BALANCES_TAB ? (
        <BalancesTable
          rows={balances}
          onRecordPayment={(row) => setPaymentFor(row)}
          onViewHistory={(row) => setHistoryFor(row)}
        />
      ) : isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : visibleCars.length === 0 ? (
        <p className="text-muted-foreground">No vehicles in this view.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleCars.map((c) => (
            <FleetCard
              key={c.id}
              c={c}
              onOpen={() => navigate(`/mechanic/vehicle/rental/${c.id}`)}
              onAssign={(e) => { e.stopPropagation(); setAssignFor(c); }}
            />
          ))}
        </div>
      )}

      {/* Assign car → company dialog */}
      <AssignDialog
        open={!!assignFor}
        car={assignFor}
        companies={companies}
        onClose={() => setAssignFor(null)}
        onSave={(cid) => assign.mutate({ ref_id: assignFor.id, company_id: cid })}
        saving={assign.isPending}
      />

      {/* Manage companies dialog */}
      <ManageCompaniesDialog open={manageOpen} onClose={() => setManageOpen(false)} />

      {/* Record payment dialog */}
      <PaymentDialog
        open={!!paymentFor}
        row={paymentFor}
        onClose={() => setPaymentFor(null)}
      />

      {/* Payment history dialog */}
      <HistoryDialog
        open={!!historyFor}
        row={historyFor}
        onClose={() => setHistoryFor(null)}
      />
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function FleetCard({ c, onOpen, onAssign }) {
  return (
    <Card className="overflow-hidden cursor-pointer hover:border-primary/50 transition-colors" data-testid={`rental-card-${c.id}`} onClick={onOpen}>
      {c.needs_service && (
        <div className="bg-red-600 text-white text-center text-xs font-bold py-1.5 tracking-wide flex items-center justify-center gap-1" data-testid={`needs-service-${c.id}`}>
          <AlertTriangle className="h-3.5 w-3.5" /> NEEDS SERVICE
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <Car className="h-4 w-4 text-muted-foreground shrink-0" />
            <p className="font-heading font-semibold truncate">{c.label}</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground font-mono-data mb-2">{c.plate || "—"} · {c.vehicle_class || ""}</p>
        <div className="flex items-center gap-2 mb-3">
          {c.company_name ? (
            <Badge variant={c.company_billable ? "default" : "secondary"} className="text-[10px]" data-testid={`car-company-${c.id}`}>
              <Building2 className="h-3 w-3 mr-1" />{c.company_name}{!c.company_billable && " · not billed"}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-600/40">Unassigned</Badge>
          )}
          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs ml-auto" onClick={onAssign} data-testid={`assign-btn-${c.id}`}>
            <Pencil className="h-3 w-3 mr-1" /> Assign
          </Button>
        </div>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Last oil change</span><span>{fmtDate(c.last_oil_change)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Days since</span><span className={c.needs_service ? "text-red-400 font-semibold" : ""}>{c.days_since_oil ?? "—"}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Repairs logged</span><span>{c.work_order_count}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Total spent</span><span className="font-mono-data">{money(c.total_spent)}</span></div>
        </div>
      </div>
    </Card>
  );
}

function BalancesTable({ rows, onRecordPayment, onViewHistory }) {
  const billable = rows.filter((r) => r.billable && r.company_id);
  const nonBillable = rows.filter((r) => !r.billable && r.company_id);
  const unassigned = rows.find((r) => !r.company_id);
  const grandBalance = billable.reduce((s, r) => s + r.balance, 0);

  return (
    <div className="space-y-6" data-testid="balances-view">
      <Card className="p-5 bg-primary/5 border-primary/30">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground mb-1">TOTAL OUTSTANDING (all billable companies)</p>
            <p className="font-heading text-3xl font-bold" data-testid="balances-grand-total">{money(grandBalance)}</p>
          </div>
          <DollarSign className="h-10 w-10 text-primary/40" />
        </div>
      </Card>

      <div className="border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/30">
            <tr>
              <th className="text-left px-4 py-3 label-mono font-normal">Company</th>
              <th className="text-right px-4 py-3 label-mono font-normal">Cars</th>
              <th className="text-right px-4 py-3 label-mono font-normal">Jobs</th>
              <th className="text-right px-4 py-3 label-mono font-normal">Billed</th>
              <th className="text-right px-4 py-3 label-mono font-normal">Paid</th>
              <th className="text-right px-4 py-3 label-mono font-normal">Balance</th>
              <th className="text-right px-4 py-3 label-mono font-normal w-64">Actions</th>
            </tr>
          </thead>
          <tbody>
            {billable.length === 0 && (
              <tr><td colSpan={7} className="text-center text-muted-foreground p-6">No billable companies yet.</td></tr>
            )}
            {billable.map((r) => (
              <tr key={r.company_id} className="border-t border-border hover:bg-secondary/20" data-testid={`balance-row-${r.company_id}`}>
                <td className="px-4 py-3 font-medium">{r.name}</td>
                <td className="px-4 py-3 text-right">{r.car_count}</td>
                <td className="px-4 py-3 text-right">{r.job_count}</td>
                <td className="px-4 py-3 text-right font-mono-data">{money(r.total_billed)}</td>
                <td className="px-4 py-3 text-right font-mono-data text-emerald-400">{money(r.total_paid)}</td>
                <td className={`px-4 py-3 text-right font-mono-data font-semibold ${r.balance > 0.01 ? "text-red-400" : r.balance < -0.01 ? "text-emerald-400" : ""}`} data-testid={`balance-amount-${r.company_id}`}>
                  {money(r.balance)}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => onRecordPayment(r)} disabled={r.balance <= 0.005 && r.total_billed === 0} data-testid={`record-payment-${r.company_id}`}>
                    <Plus className="h-3 w-3 mr-1" /> Payment
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onViewHistory(r)} data-testid={`view-history-${r.company_id}`}>
                    History
                  </Button>
                </td>
              </tr>
            ))}
            {nonBillable.length > 0 && (
              <tr className="border-t border-border">
                <td colSpan={7} className="px-4 py-2 text-xs text-muted-foreground uppercase tracking-wide bg-secondary/10">Not billed</td>
              </tr>
            )}
            {nonBillable.map((r) => (
              <tr key={r.company_id} className="border-t border-border hover:bg-secondary/10 opacity-70">
                <td className="px-4 py-3 font-medium">{r.name} <Badge variant="secondary" className="ml-2 text-[10px]">not billed</Badge></td>
                <td className="px-4 py-3 text-right">{r.car_count}</td>
                <td className="px-4 py-3 text-right text-muted-foreground">—</td>
                <td className="px-4 py-3 text-right text-muted-foreground">—</td>
                <td className="px-4 py-3 text-right text-muted-foreground">—</td>
                <td className="px-4 py-3 text-right text-muted-foreground">—</td>
                <td className="px-4 py-3 text-right"></td>
              </tr>
            ))}
            {unassigned && unassigned.car_count > 0 && (
              <tr className="border-t border-border">
                <td colSpan={2} className="px-4 py-3 text-amber-400 font-medium">{unassigned.name}</td>
                <td colSpan={4} className="px-4 py-3 text-xs text-muted-foreground">{unassigned.car_count} car(s) not tied to any company</td>
                <td className="px-4 py-3 text-right"></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AssignDialog({ open, car, companies, onClose, onSave, saving }) {
  const [cid, setCid] = useState("");
  const NONE = "__none__";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="assign-dialog">
        <DialogHeader><DialogTitle>Assign car to company</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground mb-3">{car?.label} · {car?.plate}</p>
        <div>
          <Label className="label-mono text-xs mb-1.5 block">Owning company</Label>
          <Select value={cid || car?.company_id || NONE} onValueChange={(v) => setCid(v === NONE ? "" : v)}>
            <SelectTrigger data-testid="assign-company-select"><SelectValue placeholder="Choose company" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>— Unassigned</SelectItem>
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}{!c.billable ? " (not billed)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(cid === "" ? "" : (cid || car?.company_id || ""))} disabled={saving} data-testid="assign-save-btn">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageCompaniesDialog({ open, onClose }) {
  const qc = useQueryClient();
  const { data: companies = [] } = useQuery({
    queryKey: ["mech-companies"],
    queryFn: async () => (await api.get("/companies")).data,
    enabled: open,
  });
  const [editing, setEditing] = useState(null); // {id?, name, billable, notes}
  const empty = { name: "", billable: true, notes: "" };

  const save = useMutation({
    mutationFn: async (payload) => {
      if (payload.id) return api.put(`/companies/${payload.id}`, payload);
      return api.post(`/companies`, payload);
    },
    onSuccess: () => {
      toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["mech-companies"] });
      qc.invalidateQueries({ queryKey: ["mech-balances"] });
      qc.invalidateQueries({ queryKey: ["mech-rental"] });
      setEditing(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/companies/${id}`),
    onSuccess: () => {
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["mech-companies"] });
      qc.invalidateQueries({ queryKey: ["mech-balances"] });
      qc.invalidateQueries({ queryKey: ["mech-rental"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="manage-companies-dialog">
        <DialogHeader><DialogTitle>Manage companies</DialogTitle></DialogHeader>

        {!editing ? (
          <>
            <div className="border border-border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary/30">
                  <tr>
                    <th className="text-left px-3 py-2 label-mono font-normal">Company</th>
                    <th className="text-left px-3 py-2 label-mono font-normal">Charge for work?</th>
                    <th className="text-right px-3 py-2 label-mono font-normal">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.length === 0 && (
                    <tr><td colSpan={3} className="text-center text-muted-foreground p-4">No companies yet — add the first one below.</td></tr>
                  )}
                  {companies.map((c) => (
                    <tr key={c.id} className="border-t border-border" data-testid={`company-row-${c.id}`}>
                      <td className="px-3 py-2 font-medium">{c.name}{c.notes ? <span className="block text-xs text-muted-foreground">{c.notes}</span> : null}</td>
                      <td className="px-3 py-2">
                        {c.billable ? <Badge>Yes · billed</Badge> : <Badge variant="secondary">No · free of charge</Badge>}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(c)} data-testid={`edit-company-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => { if (confirm(`Delete "${c.name}"?`)) del.mutate(c.id); }} data-testid={`delete-company-${c.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Close</Button>
              <Button onClick={() => setEditing(empty)} data-testid="add-company-btn"><Plus className="h-4 w-4 mr-1" /> Add company</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-3">
              <div>
                <Label className="label-mono text-xs mb-1.5 block">Company name</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Turo Group, Sunny Rentals" data-testid="company-name-input" />
              </div>
              <div className="flex items-start gap-3 border border-border rounded-md p-3">
                <Checkbox
                  checked={editing.billable}
                  onCheckedChange={(v) => setEditing({ ...editing, billable: !!v })}
                  data-testid="company-billable-checkbox"
                />
                <div>
                  <div className="text-sm font-medium">Charge this company for work done</div>
                  <div className="text-xs text-muted-foreground">Turn OFF for companies you service free of charge. Their work orders won&apos;t appear on the Balances tab.</div>
                </div>
              </div>
              <div>
                <Label className="label-mono text-xs mb-1.5 block">Notes (optional)</Label>
                <Textarea rows={2} value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} placeholder="Contact person, phone, terms…" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={() => save.mutate(editing)} disabled={save.isPending || !editing.name.trim()} data-testid="company-save-btn">
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PaymentDialog({ open, row, onClose }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  // Reset when opening
  const key = row?.company_id;
  useMemo(() => {
    if (open && row) {
      setAmount(row.balance > 0 ? row.balance.toFixed(2) : "");
      setMethod("");
      setNote("");
      setDate(new Date().toISOString().slice(0, 10));
    }
  }, [open, key]);

  const pay = useMutation({
    mutationFn: (payload) => api.post(`/companies/${row.company_id}/payments`, payload),
    onSuccess: () => {
      toast.success("Payment recorded");
      qc.invalidateQueries({ queryKey: ["mech-balances"] });
      qc.invalidateQueries({ queryKey: ["mech-owner-payments", row.company_id] });
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const clearFull = () => {
    if (!row || row.balance <= 0.005) return;
    pay.mutate({ amount: row.balance, date, method: method || "clear", note: note || "Balance cleared" });
  };

  if (!row) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="payment-dialog">
        <DialogHeader><DialogTitle>Record payment · {row.name}</DialogTitle></DialogHeader>
        <div className="border border-border rounded-md p-3 mb-2 bg-secondary/20">
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Currently owed</span><span className="font-mono-data font-semibold">{money(row.balance)}</span></div>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="label-mono text-xs mb-1.5 block">Amount</Label>
            <Input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payment-amount-input" />
          </div>
          <div>
            <Label className="label-mono text-xs mb-1.5 block">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="payment-date-input" />
          </div>
          <div>
            <Label className="label-mono text-xs mb-1.5 block">Method (optional)</Label>
            <Input value={method} onChange={(e) => setMethod(e.target.value)} placeholder="Cash, e-transfer, cheque…" />
          </div>
          <div>
            <Label className="label-mono text-xs mb-1.5 block">Note (optional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {row.balance > 0.005 && (
            <Button variant="outline" onClick={clearFull} disabled={pay.isPending} className="sm:mr-auto" data-testid="clear-balance-btn">
              <Check className="h-4 w-4 mr-1" /> Clear full balance
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => pay.mutate({ amount: Number(amount) || 0, date, method, note })}
            disabled={pay.isPending || !Number(amount)}
            data-testid="payment-save-btn"
          >
            {pay.isPending ? "Saving…" : "Record payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ open, row, onClose }) {
  const qc = useQueryClient();
  const { data: payments = [], isLoading } = useQuery({
    queryKey: ["mech-owner-payments", row?.company_id],
    queryFn: async () => (await api.get(`/companies/${row.company_id}/payments`)).data,
    enabled: !!(open && row?.company_id),
  });
  const del = useMutation({
    mutationFn: (pid) => api.delete(`/companies/${row.company_id}/payments/${pid}`),
    onSuccess: () => {
      toast.success("Payment removed");
      qc.invalidateQueries({ queryKey: ["mech-owner-payments", row.company_id] });
      qc.invalidateQueries({ queryKey: ["mech-balances"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  if (!row) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="history-dialog">
        <DialogHeader><DialogTitle>Payment history · {row.name}</DialogTitle></DialogHeader>
        {isLoading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : payments.length === 0 ? (
          <p className="text-muted-foreground text-sm">No payments recorded yet.</p>
        ) : (
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/30">
                <tr>
                  <th className="text-left px-3 py-2 label-mono font-normal">Date</th>
                  <th className="text-left px-3 py-2 label-mono font-normal">Method</th>
                  <th className="text-left px-3 py-2 label-mono font-normal">Note</th>
                  <th className="text-right px-3 py-2 label-mono font-normal">Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-t border-border" data-testid={`payment-row-${p.id}`}>
                    <td className="px-3 py-2 font-mono-data text-xs">{fmtDate(p.date)}</td>
                    <td className="px-3 py-2">{p.method || "—"}</td>
                    <td className="px-3 py-2 text-muted-foreground text-xs">{p.note || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-data">{money(p.amount)}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => { if (confirm("Delete this payment?")) del.mutate(p.id); }} data-testid={`del-payment-${p.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
